import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import {
  HomeSimpleDoor, Reports, Sparks, LightBulb, Activity, GitCompare, Settings as SettingsIcon, LogOut, MagicWand, CreditCard, Network, GraphUp, Building, Folder,
} from "iconoir-react";
import NotificationsBell from "@/components/NotificationsBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import OrgSwitcher from "@/components/OrgSwitcher";
import ProBadge from "@/components/ProBadge";

const NavItem = ({ to, icon: Icon, label, testid, locked = false }) => (
  <NavLink
    to={to}
    data-testid={testid}
    end
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 font-app text-sm border-l-2 transition-colors ${
        isActive ? "border-intl bg-card text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-card/60"
      }`
    }
  >
    <Icon width={18} height={18} strokeWidth={1.6} />
    <span>{label}</span>
    {locked && <ProBadge />}
  </NavLink>
);

export default function AppShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isFree = (user?.plan || "free") === "free";
  const isAdmin = user?.role === "admin";
  // Admins always get full access; free users see PRO badges on premium features.
  const gated = isFree && !isAdmin;

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-shell">
      <aside className="w-64 shrink-0 border-r border-border flex flex-col" data-testid="app-sidebar">
        <Link to="/dashboard" className="flex items-center gap-3 px-6 h-16 border-b border-border">
          <div className="w-3 h-3 bg-intl" />
          <span className="font-app font-bold">AI Visibility OS</span>
        </Link>

        <nav className="flex-1 py-4 space-y-1">
          <NavItem to="/dashboard" icon={HomeSimpleDoor} label={t("app.dashboard")} testid="nav-dashboard" />
          <NavItem to="/audits/new" icon={Reports} label={t("app.newAudit")} testid="nav-new-audit" />
          <NavItem to="/fixes" icon={MagicWand} label={t("app.fixes")} testid="nav-fixes" locked={gated} />
          <NavItem to="/content" icon={Sparks} label={t("app.content")} testid="nav-content" locked={gated} />
          <NavItem to="/competitors" icon={GitCompare} label={t("app.competitors")} testid="nav-competitors" locked={gated} />
          <NavItem to="/monitoring" icon={Activity} label={t("app.monitoring")} testid="nav-monitoring" locked={gated} />
          <div className="mt-6 px-6 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Workspace</div>
          <NavItem to="/organizations" icon={Building} label="Organizations" testid="nav-organizations" />
          <NavItem to="/projects" icon={Folder} label="Projects" testid="nav-projects" />
          <div className="mt-6 px-6 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("app.account")}</div>
          <NavItem to="/billing" icon={CreditCard} label={t("app.billing")} testid="nav-billing" />
          <NavItem to="/settings" icon={SettingsIcon} label={t("app.settings")} testid="nav-settings" />
          {isAdmin && (
            <NavItem to="/integrations" icon={Network} label="Integrations" testid="nav-integrations" />
          )}
          {isAdmin && (
            <NavItem to="/admin/analytics" icon={GraphUp} label="Analytics" testid="nav-admin-analytics" />
          )}
        </nav>

        <div className="border-t border-border p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("app.signedIn")}</div>
          <div className="mt-1 text-sm truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest" data-testid="sidebar-user-plan">
            {isAdmin ? <span className="text-intl">Admin · Full access</span> : (
              isFree ? <span className="text-muted-foreground">Free plan · <Link to="/billing" className="text-intl underline">Upgrade</Link></span>
                     : <span className="text-intl">{(user?.plan || "").toUpperCase()} plan · Active</span>
            )}
          </div>
          <button
            onClick={logout}
            data-testid="sidebar-logout-button"
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 border border-border text-xs font-app uppercase tracking-widest hover:bg-obsidian hover:text-bone transition-colors"
          >
            <LogOut width={14} height={14} /> {t("app.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col" data-testid="app-main">
        <header className="h-16 border-b border-border flex items-center justify-between px-8" data-testid="app-header">
          <div>
            {subtitle && <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{subtitle}</div>}
            <h1 className="font-app text-lg font-medium" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3" data-testid="page-actions">
            <OrgSwitcher />
            <LanguageSwitcher />
            <NotificationsBell />
            {actions}
          </div>
        </header>
        <div className="p-8 lg:p-10 flex-1">{children}</div>
      </main>
    </div>
  );
}
